/**
 * Full CIWA-Ar (Clinical Institute Withdrawal Assessment - Alcohol revised)
 * scoring data — 10 items with descriptive options. Sourced from official
 * paramedic CIWA reference (per user-provided screenshots Apr 2026).
 *
 * Score range: 0–67 (max 7 per item × 9 items + 4 for orientation).
 * Severity bands match the YEMS guideline thresholds.
 */
export const CIWA_ITEMS = [
  {
    id: "nausea",
    title: "Nausea / Vomiting",
    prompt: "Ask: Do you feel sick to your stomach? Have you vomited?",
    options: [
      { score: 0, label: "No nausea, no vomiting" },
      { score: 1, label: "Mild nausea with no vomiting" },
      { score: 2, label: "(continuous mild → intermittent)" },
      { score: 3, label: "(continuous mild → intermittent)" },
      { score: 4, label: "Intermittent nausea with dry heaves" },
      { score: 5, label: "(intermittent → constant)" },
      { score: 6, label: "(intermittent → constant)" },
      { score: 7, label: "Constant nausea, frequent dry heaves and vomiting" },
    ],
  },
  {
    id: "tremor",
    title: "Tremor",
    prompt: "Have patient extend arms and spread fingers apart.",
    options: [
      { score: 0, label: "No tremor" },
      { score: 1, label: "Tremor not visible, but felt at fingertip" },
      { score: 2, label: "(mild tremor not visible)" },
      { score: 3, label: "(mild tremor not visible)" },
      { score: 4, label: "Moderate tremor with arms extended" },
      { score: 5, label: "(moderate → severe)" },
      { score: 6, label: "(moderate → severe)" },
      { score: 7, label: "Severe, even with arms not extended" },
    ],
  },
  {
    id: "sweats",
    title: "Paroxysmal Sweats",
    prompt: "Observe for sweating.",
    options: [
      { score: 0, label: "No sweat visible" },
      { score: 1, label: "Barely perceptible sweating, palms moist" },
      { score: 2, label: "(mild sweating)" },
      { score: 3, label: "(mild sweating)" },
      { score: 4, label: "Beads of sweat obvious on forehead" },
      { score: 5, label: "(more obvious sweating)" },
      { score: 6, label: "(more obvious sweating)" },
      { score: 7, label: "Drenching sweats" },
    ],
  },
  {
    id: "headache",
    title: "Headache / Fullness in Head",
    prompt: "Ask: Does your head feel different? Does it feel like there is a band around your head?",
    options: [
      { score: 0, label: "Not present" },
      { score: 1, label: "Very mild" },
      { score: 2, label: "Mild" },
      { score: 3, label: "Moderate" },
      { score: 4, label: "Moderately severe" },
      { score: 5, label: "Severe" },
      { score: 6, label: "Very severe" },
      { score: 7, label: "Extremely severe" },
    ],
  },
  {
    id: "orientation",
    title: "Orientation / Clouding of Sensorium",
    prompt: "Ask: What day is this? Where are you? Who am I?",
    options: [
      { score: 0, label: "Oriented and can do serial additions" },
      { score: 1, label: "Cannot do serial additions or is uncertain about date" },
      { score: 2, label: "Disoriented for date by no more than 2 calendar days" },
      { score: 3, label: "Disoriented for date by more than 2 calendar days" },
      { score: 4, label: "Disoriented for place and/or person" },
    ],
  },
  {
    id: "anxiety",
    title: "Anxiety",
    prompt: "Ask: Do you feel nervous?",
    options: [
      { score: 0, label: "No anxiety, at ease" },
      { score: 1, label: "Mildly anxious" },
      { score: 2, label: "More than mildly anxious" },
      { score: 3, label: "Mildly to moderately anxious" },
      { score: 4, label: "Moderately anxious, or guarded, so anxiety is inferred" },
      { score: 5, label: "Moderately to severely anxious" },
      { score: 6, label: "Severely anxious" },
      { score: 7, label: "Equivalent to acute panic states as seen in severe delirium or acute schizophrenic reactions" },
    ],
  },
  {
    id: "agitation",
    title: "Agitation",
    prompt: "Observe behaviour.",
    options: [
      { score: 0, label: "Normal activity" },
      { score: 1, label: "Somewhat more activity than normal activity" },
      { score: 2, label: "Mildly agitated" },
      { score: 3, label: "Mildly to moderately agitated" },
      { score: 4, label: "Moderately fidgety and restless" },
      { score: 5, label: "Moderately to severely agitated" },
      { score: 6, label: "Severely agitated" },
      { score: 7, label: "Paces back and forth during most of the interview, or constantly thrashes about" },
    ],
  },
  {
    id: "tactile",
    title: "Tactile Disturbances",
    prompt: "Ask: Have you any itching, pins-and-needles sensations, burning or numbness, or do you feel bugs crawling on or under your skin?",
    options: [
      { score: 0, label: "None" },
      { score: 1, label: "Very mild itching, pin and needles, burning, or numbness" },
      { score: 2, label: "Mild itching, pin and needles, burning, or numbness" },
      { score: 3, label: "Moderate itching, pin and needles, burning, or numbness" },
      { score: 4, label: "Moderately severe hallucinations" },
      { score: 5, label: "Severe hallucinations" },
      { score: 6, label: "Extremely severe hallucinations" },
      { score: 7, label: "Continuous hallucinations" },
    ],
  },
  {
    id: "auditory",
    title: "Auditory Disturbances",
    prompt: "Ask: Are you more aware of sounds around you? Are they harsh? Do they frighten you? Are you hearing anything that is disturbing you?",
    options: [
      { score: 0, label: "Not present" },
      { score: 1, label: "Very mild harshness or ability to frighten" },
      { score: 2, label: "Mild harshness or ability to frighten" },
      { score: 3, label: "Moderate harshness or ability to frighten" },
      { score: 4, label: "Moderately severe hallucinations" },
      { score: 5, label: "Severe hallucinations" },
      { score: 6, label: "Extremely severe hallucinations" },
      { score: 7, label: "Continuous hallucinations" },
    ],
  },
  {
    id: "visual",
    title: "Visual Disturbances",
    prompt: "Ask: Does the light appear too bright? Is its colour different? Does it hurt your eyes? Are you seeing anything disturbing?",
    options: [
      { score: 0, label: "Not present" },
      { score: 1, label: "Very mild sensitivity" },
      { score: 2, label: "Mild sensitivity" },
      { score: 3, label: "Moderate sensitivity" },
      { score: 4, label: "Moderately severe hallucinations" },
      { score: 5, label: "Severe hallucinations" },
      { score: 6, label: "Extremely severe hallucinations" },
      { score: 7, label: "Continuous hallucinations" },
    ],
  },
];

export const CIWA_VARIANT_TREATMENT = {
  ground: {
    minimal: { range: "0–9",   label: "Minimal", treatment: "Continue assessment q15min. No medication required." },
    mild:    { range: "10–14", label: "Mild",    treatment: "Diazepam 5–10 mg PO/IV. Reassess q15min." },
    moderate:{ range: "15–19", label: "Moderate",treatment: "Diazepam 10–20 mg IV. Reassess q15min. Consider transport." },
    severe:  { range: "≥20",   label: "Severe",  treatment: "Diazepam 20 mg IV q15min until CIWA <10. Transport." },
  },
  air: {
    minimal: { range: "0–9",   label: "Minimal", treatment: "Continue assessment q15min." },
    mild:    { range: "10–14", label: "Mild",    treatment: "Lorazepam 1–2 mg IV/IM/PO. Reassess q15min." },
    moderate:{ range: "15–19", label: "Moderate",treatment: "Lorazepam 2–4 mg IV. Reassess q15min." },
    severe:  { range: "≥20",   label: "Severe",  treatment: "Lorazepam 4 mg IV q15min until CIWA <10." },
  },
  wes: {
    minimal: { range: "0–1",   label: "Minimal", treatment: "Continue assessment. Standard supportive care, IV NS TKVO, limit stimulation, NPO." },
    mild:    { range: "2–9",   label: "Mild",    treatment: "Lorazepam 1 mg PO q30min. Reassess CIWA q30min." },
    moderate:{ range: "10–19", label: "Moderate",treatment: "Diazepam 2.5–5 mg IV/IM q30min until CIWA <10. Reassess." },
    severe:  { range: "≥20",   label: "Severe",  treatment: "Diazepam 2.5–5 mg IV/IM q30min until CIWA <10. Activate hospital transport if no change after 10 mg total or ≈1 hour of treatment." },
  },
};

// CIWA severity bands differ for PCP-PS — the YEMS Paramedic Specialist
// guideline keys medication off CIWA 2 (Lorazepam) and CIWA 10 (Diazepam),
// not the standard 0/10/15/20 ground/air bands.
export function ciwaSeverityForVariant(total, variant = "ground") {
  if (variant === "wes") {
    if (total <= 1)  return "minimal";
    if (total <= 9)  return "mild";
    if (total <= 19) return "moderate";
    return "severe";
  }
  return ciwaSeverity(total);
}

export function ciwaSeverity(total) {
  if (total <= 9)  return "minimal";
  if (total <= 14) return "mild";
  if (total <= 19) return "moderate";
  return "severe";
}
